import { ComponentProps, ReactNode, useState } from 'react';
import { useQueryClient } from 'react-query';
import Table from 'components/layout/Table';
import NoCompaniesInGroupPlaceholder from 'components/widgets/Dashboard/CompanyGroups/NoCompaniesInGroupPlaceholder';
import { companiesInCompanyGroupConfigQueryKey } from 'hooks/useFetchCompanyLeads';
import { useToast } from 'hooks/useToast';
import TableBody from 'pages/Dashboard/CompanyOverview/CompanyTable/Body';
import TableHeader from 'pages/Dashboard/CompanyOverview/CompanyTable/TableHeader';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import { CompanyLead } from 'types';

const tableStyle = {
  border: 'none',
  overflowX: 'unset',
  overflowY: 'unset',
  height: '100%',
  width: '100%',
};

type CompanySortKey = 'name' | 'activeProjects';
type CompanySortDirection = 'asc' | 'desc';

type Props = {
  items: CompanyLead[];
  groupConfigId: number;
  setCompanyMenu?: (companyMenu: boolean) => void;
  setCurrentCompany?: (companyId: number) => void;
  setCurrentCompanyUuid?: (companyUuid: string) => void;
  setCurrentCompanyName?: (companyName: string) => void;
  companyMenu?: boolean;
  sortKey?: CompanySortKey;
  sortDirection?: CompanySortDirection;
  onSortColumn?: (column: CompanySortKey) => void;
  header?: ReactNode;
  footer?: ReactNode;
  cardSx?: ComponentProps<typeof Table>['cardSx'];
};

const CompanyTable = ({
  items,
  groupConfigId,
  setCompanyMenu,
  companyMenu,
  setCurrentCompany,
  setCurrentCompanyUuid,
  setCurrentCompanyName,
  sortKey,
  sortDirection,
  onSortColumn,
  header,
  footer,
  cardSx,
}: Props) => {
  const { renderCustomToast: toast } = useToast();
  const queryClient = useQueryClient();
  const [isRemoving, setIsRemoving] = useState(false);

  if (!items) return null;

  if (!items.length) {
    return <NoCompaniesInGroupPlaceholder groupId={groupConfigId} />;
  }

  const handleRowClick = (company: CompanyLead) => {
    if (setCompanyMenu) {
      setCompanyMenu(!companyMenu);
    }
    if (setCurrentCompany) {
      setCurrentCompany(company.id);
    }
    if (setCurrentCompanyUuid) {
      setCurrentCompanyUuid(company.uuid);
    }
    if (setCurrentCompanyName) {
      setCurrentCompanyName(company.name);
    }
  };

  const handleRemoveFromGroup = async (company: CompanyLead) => {
    if (isRemoving) return;
    setIsRemoving(true);
    try {
      await GroupConfigurationAPI.deleteCompaniesFromGroupConfig(groupConfigId, [
        company.id,
      ]);
      toast({
        title: 'Company removed from group',
        status: 'success',
      });
      await queryClient.invalidateQueries({
        queryKey: companiesInCompanyGroupConfigQueryKey(groupConfigId),
      });
    } catch {
      toast({
        title: 'Failed to remove company from group',
        status: 'error',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Table
      sx={tableStyle}
      aria-label="company overview table"
      header={header}
      footer={footer}
      cardSx={cardSx}
    >
      <TableHeader
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortColumn={onSortColumn}
      />
      <TableBody
        groupId={groupConfigId}
        items={items}
        onRowClick={handleRowClick}
        onRemoveFromGroup={handleRemoveFromGroup}
      />
    </Table>
  );
};

export default CompanyTable;
